import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { Shield } from 'lucide-react';
import { auditLogsAPI } from '../../services/api';
import { PaginationControls } from '../../components/PaginationControls';

export function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { skip: (page - 1) * limit, limit };
      if (entityTypeFilter && entityTypeFilter !== 'all') params.entity_type = entityTypeFilter;
      const response = await auditLogsAPI.list(params);
      setLogs(response.data.items || response.data || []);
      setTotal(response.data.total ?? (response.data.length || 0));
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  }, [entityTypeFilter, page, limit]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setPage(1);
  }, [entityTypeFilter, limit]);

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">
          Audit Logs
        </h1>
        <p className="text-muted-foreground">Track all system changes and activities</p>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg font-heading uppercase">Activity Log</CardTitle>
            </div>
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger className="w-[150px]" data-testid="audit-entity-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="user">Users</SelectItem>
                <SelectItem value="inquiry">Inquiries</SelectItem>
                <SelectItem value="costing">Costings</SelectItem>
                <SelectItem value="quotation">Quotations</SelectItem>
                <SelectItem value="part">Parts</SelectItem>
                <SelectItem value="order">Sales orders</SelectItem>
                <SelectItem value="process_flow">Process flows</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              {logs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No audit logs found</p>
              ) : (
                logs.map((log) => (
                  <div key={log.id} className="flex items-start gap-4 p-4 border rounded-lg hover:bg-muted/30 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline">{log.entity_type}</Badge>
                        <span className="font-medium">{log.action}</span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        By {log.user_name || 'System'} • {new Date(log.timestamp).toLocaleString()}
                      </p>
                      {log.changes && Object.keys(log.changes).length > 0 && (
                        <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-x-auto">
                          {JSON.stringify(log.changes, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          <PaginationControls
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onLimitChange={setLimit}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}

export default AuditLogsPage;
